import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import type { DispatchColor } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { BLANK_LABEL } from "@/hooks/dispatch/adhocConditions";
import { allShownTicked, checklistRows, selectAllShown, toggleValue } from "@/hooks/dispatch/valueChecklist";
import type { ValueOption } from "@/hooks/dispatch/valueOptions";
import { zinc } from "@/ui/tokens";
import { DISPATCH_COLOR_VALUES } from "./dispatchColors";

/** The text box look the filter editors share. */
export const editorInputSx = {
  height: 30,
  px: 1,
  borderRadius: 1,
  bgcolor: zinc[700],
  color: "white",
  fontSize: "0.8125rem",
  "& input": { p: 0 },
} as const;

/** The Apply and Clear pair every editor ends with. */
export function EditorActions({
  canApply,
  canClear,
  onApply,
  onClear,
}: {
  canApply: boolean;
  canClear: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
      <ButtonBase
        data-sb={TestIds.dispatch.columnFilterApply}
        disabled={!canApply}
        onClick={onApply}
        sx={{
          flex: 1,
          height: 30,
          borderRadius: 1,
          fontSize: "0.8125rem",
          bgcolor: "var(--color-brand-primary-600)",
          color: "white",
          "&:hover": { bgcolor: "var(--color-brand-primary-500)" },
          "&:disabled": { opacity: 0.5 },
        }}
      >
        Apply
      </ButtonBase>
      <ButtonBase
        data-sb={TestIds.dispatch.columnFilterClear}
        disabled={!canClear}
        onClick={onClear}
        sx={{
          flex: 1,
          height: 30,
          borderRadius: 1,
          fontSize: "0.8125rem",
          color: zinc[300],
          bgcolor: zinc[700],
          "&:hover": { bgcolor: zinc[600], color: "white" },
          "&:disabled": { opacity: 0.5 },
        }}
      >
        Clear
      </ButtonBase>
    </Box>
  );
}

/** One checkbox row in the values checklist: a value, Select all, or (blank). */
function ValueRow({
  checked,
  indeterminate = false,
  label,
  count,
  color,
  muted = false,
  testId,
  onToggle,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  count?: number;
  color?: DispatchColor;
  /** Set for (blank) and Select all, which are choices about the values rather than values themselves. */
  muted?: boolean;
  testId: string;
  onToggle: () => void;
}) {
  return (
    <ButtonBase
      data-sb={testId}
      aria-pressed={indeterminate ? "mixed" : checked}
      onClick={onToggle}
      sx={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        gap: 0.5,
        px: 0.25,
        borderRadius: 1,
        fontSize: "0.8125rem",
        color: zinc[200],
        textAlign: "left",
        "&:hover": { bgcolor: zinc[700] },
      }}
    >
      <Checkbox size="small" checked={checked} indeterminate={indeterminate} tabIndex={-1} sx={{ p: 0.5 }} />
      {color && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: DISPATCH_COLOR_VALUES[color], flex: "none" }}
        />
      )}
      <Box
        component="span"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...(muted ? { fontStyle: "italic", color: zinc[400] } : {}),
        }}
      >
        {label}
      </Box>
      {count !== undefined && (
        <Box component="span" sx={{ color: zinc[500], fontVariantNumeric: "tabular-nums", pr: 0.5 }}>
          {count}
        </Box>
      )}
    </ButtonBase>
  );
}

/** The (blank) row a checklist offers for the rows with no value, and its tick, which the caller owns. */
type BlankChoice = { count: number; checked: boolean; onChange: (checked: boolean) => void };

/**
 * The checklist the values editors share: a search box, Select all, and a checkbox per value with its count.
 *
 * (blank), for the rows with no value, is listed where the caller offers it. Select all ticks every row the search
 * shows, (blank) included, or unticks them when all are ticked already; a single-value checklist has none, and a tick
 * there replaces the last. A value a stale condition names but the rows no longer carry stays listed so it can be
 * unticked. The checklist owns the search; the caller owns the ticks, so they change nothing until it applies them.
 */
export function ValueChecklist({
  options,
  chosen,
  onChosenChange,
  multiple,
  blank,
  autoFocus,
}: {
  options: readonly ValueOption[];
  chosen: ReadonlySet<string>;
  onChosenChange: (next: Set<string>) => void;
  multiple: boolean;
  /** The (blank) row; absent where the caller offers none. */
  blank?: BlankChoice;
  /** Whether the search box takes focus as the checklist mounts. */
  autoFocus: boolean;
}) {
  const [search, setSearch] = useState("");
  if (options.length === 0 && !blank) {
    return <Typography sx={{ fontSize: "0.75rem", color: zinc[500], px: 0.5 }}>No values to choose from.</Typography>;
  }
  const rows = checklistRows(options, chosen, search, blank?.checked);
  const allTicked = allShownTicked(rows);
  const toggleAll = () => {
    const next = selectAllShown(chosen, rows, blank?.checked ?? false);
    onChosenChange(next.chosen);
    blank?.onChange(next.blank);
  };
  return (
    <>
      <Box sx={{ ...editorInputSx, display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <SearchIcon sx={{ fontSize: 16, color: zinc[400] }} />
        <InputBase
          autoFocus={autoFocus}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Find a value"
          inputProps={{ "aria-label": "Find a value" }}
          sx={{ flex: 1, color: "white", fontSize: "inherit" }}
        />
      </Box>
      {multiple && rows.shown > 0 && (
        <ValueRow
          checked={allTicked}
          indeterminate={rows.ticked > 0 && !allTicked}
          label={search ? "Select all shown" : "Select all"}
          muted
          testId={TestIds.dispatch.columnFilterSelectAll}
          onToggle={toggleAll}
        />
      )}
      <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
        {rows.listed.map((option) => (
          <ValueRow
            key={option.value}
            checked={chosen.has(option.value)}
            label={option.label}
            count={option.count}
            color={option.color}
            testId={TestIds.dispatch.columnFilterValue}
            onToggle={() => onChosenChange(toggleValue(chosen, option.value, multiple))}
          />
        ))}
        {blank && rows.blankShown && (
          <ValueRow
            checked={blank.checked}
            label={BLANK_LABEL}
            count={blank.count}
            muted
            testId={TestIds.dispatch.columnFilterBlank}
            onToggle={() => blank.onChange(!blank.checked)}
          />
        )}
      </Box>
    </>
  );
}
